import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { useAppStateContext } from 'src/AppState/AppState';
import { PageContainer } from 'src/components/CustomersBar/CustomerTab/ResponsibleGamblingTab/ResponsibleGamblingTabs.style';
import { BadgeColored } from 'src/components/CustomersBar/CustomerTab/CustomerSummaryTab/CustomerSummaryTab.style';
import { Button } from 'src/domains/common/components/button/button';
import { LoadingV2 } from 'src/components/Loading/LoadingV2';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { ReactivateSelfExclusionState, SelfExclusionEnum } from './ReactivateSelfExclusion.state';
import { MatchedExclusionsTable } from './MatchedExclusionsTable/MatchedExclusionsTable';

interface ReactivateSelfExclusionType {
    customerId: number;
    permissionSelfExclusion?: boolean;
    onAuditClick?: () => void;
}

const getStatusBadge = (type: SelfExclusionEnum): { label: string; color: string } => {
    switch (type) {
        case SelfExclusionEnum.SELF_EXCLUDED:
            return { label: 'Self-Excluded', color: 'red' };
        case SelfExclusionEnum.INDEFINITE:
            return { label: 'Indefinite', color: 'red' };
        case SelfExclusionEnum.SELF_EXCLUSION_EXPIRED:
            return { label: 'Expired', color: 'amber' };
        case SelfExclusionEnum.COOLING_OFF:
            return { label: 'Cooling Off', color: 'amber' };
        case SelfExclusionEnum.COOLING_OFF_PASSED:
            return { label: 'Cooling Off Passed', color: 'green' };
        case SelfExclusionEnum.NOT_SELF_EXCLUDED:
        default:
            return { label: 'No', color: 'green' };
    }
};

const getExpiryLabel = (state: ReactivateSelfExclusionState): string | null => {
    switch (state.selfExclusionType) {
        case SelfExclusionEnum.SELF_EXCLUDED:
        case SelfExclusionEnum.SELF_EXCLUSION_EXPIRED:
            return state.selfExclusionOptions.expiryDate === null
                ? null
                : state.selfExclusionOptions.expiryDate.format('DD/MM/YYYY');
        case SelfExclusionEnum.INDEFINITE:
            return 'never';
        default:
            return null;
    }
};

const renderInlineButton = (state: ReactivateSelfExclusionState): React.ReactElement | null => {
    switch (state.selfExclusionType) {
        case SelfExclusionEnum.INDEFINITE:
        case SelfExclusionEnum.SELF_EXCLUSION_EXPIRED:
            return (
                <div className='ml-[40px]'>
                    <Button onClick={state.onReactivationRequest}>Request for Reactivation</Button>
                </div>
            );
        case SelfExclusionEnum.COOLING_OFF_PASSED:
            return (
                <div className='ml-[40px]'>
                    <Button onClick={state.onReactivate}>Reactivate Account</Button>
                </div>
            );
        default:
            return null;
    }
};

const renderContent = (state: ReactivateSelfExclusionState): React.ReactElement | null => {
    switch (state.selfExclusionType) {
        case SelfExclusionEnum.COOLING_OFF:
            if (state.selfExclusionOptions.coolingOffEnds === null) {
                return null;
            }

            return (
                <p>
                    Customer has requested to have his account reactivated. Cooling-off period ends on{' '}
                    <strong>{state.selfExclusionOptions.coolingOffEnds.format('DD/MM/YYYY')}</strong>.
                </p>
            );
        default:
            return null;
    }
};

export const ReactivateSelfExclusion = observer(
    'ComplianceRestriction',
    ({ customerId, permissionSelfExclusion, onAuditClick }: ReactivateSelfExclusionType) => {
        const { sdkStaff, models, common } = useAppStateContext();
        const { complianceNew } = ConfigState.get(common).config;
        const [state] = React.useState(
            () =>
                new ReactivateSelfExclusionState(sdkStaff, customerId, models, common, permissionSelfExclusion ?? false)
        );

        if (state.isLoading) {
            return (
                <LoadingV2
                    dataTest='self-exclusion-loading'
                    title='Loading Self Exclusion data...'
                />
            );
        }

        const badge = getStatusBadge(state.selfExclusionType);
        const expiryLabel = getExpiryLabel(state);

        return (
            <PageContainer>
                <header className='flex items-center justify-between flex-[0_0_36px]'>
                    <h2
                        className='header text-foreground'
                        data-test='page-title'
                    >
                        Self Exclusion
                    </h2>
                    <div className='flex items-center gap-2'>
                        {complianceNew && onAuditClick !== undefined && (
                            <Button
                                onClick={onAuditClick}
                                variant='secondary'
                                dataTest='self-exclusion-audit-button'
                            >
                                Self Exclusion Audit
                            </Button>
                        )}
                        <Button
                            icon='plus'
                            onClick={state.handleSetExclusionPeriod}
                            variant='primary'
                            dataTest='set-exclusion-period-button'
                            disabled={state.selfExclusionType !== SelfExclusionEnum.NOT_SELF_EXCLUDED}
                        >
                            Set exclusion period
                        </Button>
                    </div>
                </header>

                <div className='mb-10'>
                    <div className='flex items-center'>
                        <h4
                            className='flex items-center gap-2 mt-0 mb-0'
                            data-test='status'
                        >
                            <span>Active:</span>
                            <BadgeColored
                                badgeColor={badge.color}
                                data-test='self-exclusion-status'
                            >
                                {badge.label}
                            </BadgeColored>
                        </h4>
                        {renderInlineButton(state)}
                    </div>

                    {expiryLabel !== null && (
                        <div
                            className='pt-2'
                            data-test='self-exclusion-expires'
                        >
                            Expires: <strong>{expiryLabel}</strong>
                        </div>
                    )}
                    {renderContent(state)}
                </div>

                {complianceNew && (
                    <MatchedExclusionsTable
                        isLoading={state.isOnMatchLoading}
                        rows={state.onMatchRows}
                    />
                )}
            </PageContainer>
        );
    }
);
